import { IconNames } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useState } from 'react';
import { useInterval, useMount } from 'react-use';

import { authApi, OAuthProviderConnectionStatus } from 'apis/auth';

/** Describes the status icon. */
type OAuthConnectionStatusIcon = {
  icon: IconNames;
  color: string;
  tooltip: string;
};

/** Represents the provider connection status mapped to icon properties. */
type OAuthConnectionStatus = Record<
  OAuthProviderConnectionStatus,
  OAuthConnectionStatusIcon
>;

/** The status types. */
const statusTypes: OAuthConnectionStatus = {
  CONNECTED: {
    icon: 'check-circle-filled',
    color: colors.green[500],
    tooltip: 'Connected',
  },
  CONNECTING: {
    icon: 'sync',
    color: colors.neutral[500],
    tooltip: 'Connecting..',
  },
  CONNECTION_FAILED: {
    icon: 'close-circle-filled',
    color: colors.red[500],
    tooltip: 'Connection failed',
  },
  CONNECTION_TIMEOUT: {
    icon: 'exclamation-triangle-filled',
    color: colors.red[200],
    tooltip: 'Connection timeout',
  },
  NOT_CONNECTED: {
    icon: 'exclamation-circle-filled',
    color: colors.red[500],
    tooltip: 'Not connected',
  },
};

/** The default status type. */
const defaultStatusType: OAuthConnectionStatusIcon = {
  icon: 'exclamation-circle-filled',
  color: colors.neutral[500],
  tooltip: 'Not connected',
};

/** Returns the properties of a status icon based on the status type. */
const getConnectionStatusIcon = (
  type?: OAuthProviderConnectionStatus,
): OAuthConnectionStatusIcon => {
  if (type && type in statusTypes) {
    return statusTypes[type];
  }

  return defaultStatusType;
};

/** Represents the options of the hook. */
type UseAuthenticationOptions = {
  oAuthProviderId?: string;
  oAuthStrategy?: string;
};

/** Represents the response of the hook. */
type UseAuthenticationResponse = {
  connectionStatus: OAuthProviderConnectionStatus;
  connectionStatusIcon: OAuthConnectionStatusIcon;
  handleAuthorization: () => Promise<void>;
  resetConnectionStatus: () => void;
};

/** Represents a hook that handles the OAuth consent authorization process. */
export const useAuthentication = ({
  oAuthProviderId,
  oAuthStrategy,
}: UseAuthenticationOptions): UseAuthenticationResponse => {
  const maxPolls = 30;
  const pollDelay = 1000; // every second

  const [connectionStatus, setConnectionStatus] =
    useState<OAuthProviderConnectionStatus>('NOT_CONNECTED');

  const [pollCount, setPollCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const resetPolling = () => {
    setIsPolling(false);
    setPollCount(0);
  };

  const resetConnectionStatus = () => {
    setConnectionStatus('NOT_CONNECTED');
    resetPolling();
  };

  /** The interval hook that handles the polling. */
  useInterval(
    () => {
      if (pollCount >= maxPolls) {
        setConnectionStatus('CONNECTION_TIMEOUT');
        resetPolling();
      } else {
        handleAuthStatusCall();
        setPollCount(pollCount + 1);
      }
    },
    isPolling ? pollDelay : null,
  );

  useEffect(() => {
    if (connectionStatus === 'CONNECTION_TIMEOUT') {
      ampli.settingsIntegrationsCustomCardOauthProviderAuthenticationTimedOut({
        oAuthProviderId,
        oAuthStrategy,
      });
    }

    if (connectionStatus === 'CONNECTED') {
      ampli.settingsIntegrationsCustomCardOauthProviderAuthenticationSucceeded({
        oAuthProviderId,
        oAuthStrategy,
      });
    }
  }, [connectionStatus]);

  const [getOAuthConnection] =
    authApi.useLazyReadOAuthProviderConnectionByIdQuery();

  const [getOAuthConnectionStatus] =
    authApi.useLazyReadOAuthProviderConnectionStatusByIdQuery();

  useMount(() => handleAuthStatusCall());

  /**
   * Handles the OAuth connection status call.
   *
   * The endpoint will return the status of the OAuth connection.
   */
  const handleAuthStatusCall = async () => {
    if (!oAuthProviderId) return;

    const response = await getOAuthConnectionStatus({
      id: oAuthProviderId,
    }).unwrap();

    if (isResponseWithData(response)) {
      const status = response.data.status;

      setConnectionStatus(status);

      if (status === 'CONNECTED' || status === 'NOT_CONNECTED') {
        resetPolling();
      }
    } else {
      setConnectionStatus('CONNECTION_FAILED');
      resetPolling();

      ampli.settingsIntegrationsCustomCardOauthProviderAuthenticationFailed({
        oAuthProviderId,
        oAuthStrategy,
      });
    }
  };

  /**
   * Handles the authorization process.
   *
   * The endpoint will return an URL based on the OAuth povider settings. The
   * URL will be opened in a new window and prompt the user to consent the OAuth
   * connection with the configured application scope.
   */
  const handleAuthorization = async () => {
    if (!oAuthProviderId) return;

    try {
      const response = await getOAuthConnection({
        id: oAuthProviderId,
      }).unwrap();

      if (isResponseWithData(response)) {
        const url = response.data.url;
        const windowWidth = 620;
        const windowHeight = 800;
        // centering the new window
        const left = window.innerWidth / 2 - windowWidth / 2;
        const top = window.innerHeight / 2 - windowHeight / 2;

        setConnectionStatus('CONNECTING');
        setIsPolling(true);

        window.open(
          url,
          'DixaOAuthConsent',
          `toolbar=no,
            location=no,
            status=no,
            menubar=no,
            scrollbars=yes,
            resizable=yes,
            width=${windowWidth},
            height=${windowHeight},
            left=${left},
            top=${top}`,
        );
      }
    } catch (error) {
      setConnectionStatus('CONNECTION_FAILED');
      resetPolling();

      ampli.settingsIntegrationsCustomCardOauthProviderAuthenticationFailed({
        oAuthProviderId,
        oAuthStrategy,
      });
    }
  };

  return {
    connectionStatus,
    connectionStatusIcon: getConnectionStatusIcon(connectionStatus),
    handleAuthorization,
    resetConnectionStatus,
  };
};
